/**
 * Musical task-complete celebration (Amok — music / film / arts).
 * Guitar + notes soar bottom-left → top-right, then a living glitter / note burst.
 * Phoenix celebration remains available in phoenixCelebration.js.
 */

const FLIGHT_MS = 4200
const SETTLE_MS = 2000
export const MUSICAL_CELEBRATION_MS = FLIGHT_MS + SETTLE_MS

/** Amok-friendly stage palette — blues, golds, soft greens */
const COLORS = [
  "#6391BA",
  "#29448B",
  "#46A631",
  "#F5C15A",
  "#E8A838",
  "#14b8a6",
  "#DA6223",
  "#FFFFFF",
]

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

let audioCtx = null

const getAudioContext = () => {
  if (typeof window === "undefined") return null
  const AC = window.AudioContext || window.webkitAudioContext
  if (!AC) return null
  if (!audioCtx) audioCtx = new AC()
  if (audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {})
  }
  return audioCtx
}

const playTone = (ctx, { freq, when, duration = 0.45, type = "sine", volume = 0.07 }) => {
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  const filter = ctx.createBiquadFilter()

  osc.type = type
  osc.frequency.setValueAtTime(freq, when)

  filter.type = "lowpass"
  filter.frequency.setValueAtTime(2400, when)

  gain.gain.setValueAtTime(0.0001, when)
  gain.gain.exponentialRampToValueAtTime(volume, when + 0.035)
  gain.gain.exponentialRampToValueAtTime(0.0001, when + duration)

  osc.connect(filter)
  filter.connect(gain)
  gain.connect(ctx.destination)

  osc.start(when)
  osc.stop(when + duration + 0.05)
}

/**
 * Soft ascending chime + gentle resolve at the glitter burst.
 * Call from the click that marks a task complete (keeps AudioContext unlocked).
 */
export const playMusicalCelebrationTone = () => {
  if (typeof window === "undefined") return
  if (prefersReducedMotion()) return

  try {
    const ctx = getAudioContext()
    if (!ctx) return

    const t0 = ctx.currentTime + 0.02

    // Soft takeoff arpeggio (C major — gentle, not loud)
    const lift = [
      { freq: 523.25, at: 0, dur: 0.55, vol: 0.055 }, // C5
      { freq: 659.25, at: 0.14, dur: 0.55, vol: 0.05 }, // E5
      { freq: 783.99, at: 0.28, dur: 0.6, vol: 0.048 }, // G5
      { freq: 987.77, at: 0.44, dur: 0.7, vol: 0.045 }, // B5
    ]
    lift.forEach(n => {
      playTone(ctx, {
        freq: n.freq,
        when: t0 + n.at,
        duration: n.dur,
        type: "sine",
        volume: n.vol,
      })
      // Quiet triangle layer for warmth
      playTone(ctx, {
        freq: n.freq,
        when: t0 + n.at,
        duration: n.dur * 0.9,
        type: "triangle",
        volume: n.vol * 0.35,
      })
    })

    // Soft resolve when the guitar “bursts” (~82% of flight)
    const burstAt = t0 + FLIGHT_MS * 0.82 * 0.001
    ;[523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      playTone(ctx, {
        freq,
        when: burstAt + i * 0.03,
        duration: 0.85,
        type: i % 2 === 0 ? "sine" : "triangle",
        volume: 0.04,
      })
    })
  } catch {
    // Autoplay / AudioContext blocked — fail silently
  }
}

const cubicBezier = (t, p0, p1, p2, p3) => {
  const u = 1 - t
  const tt = t * t
  const uu = u * u
  const uuu = uu * u
  const ttt = tt * t
  return {
    x: uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x,
    y: uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y,
  }
}

const cubicBezierDerivative = (t, p0, p1, p2, p3) => {
  const u = 1 - t
  return {
    dx:
      3 * u * u * (p1.x - p0.x) +
      6 * u * t * (p2.x - p1.x) +
      3 * t * t * (p3.x - p2.x),
    dy:
      3 * u * u * (p1.y - p0.y) +
      6 * u * t * (p2.y - p1.y) +
      3 * t * t * (p3.y - p2.y),
  }
}

const easeInOut = t => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2)

const drawStar = (ctx, x, y, outer, inner, points = 4) => {
  const step = Math.PI / points
  ctx.beginPath()
  for (let i = 0; i < points * 2; i += 1) {
    const r = i % 2 === 0 ? outer : inner
    const a = i * step - Math.PI / 2
    const fn = i === 0 ? ctx.moveTo : ctx.lineTo
    fn.call(ctx, x + Math.cos(a) * r, y + Math.sin(a) * r)
  }
  ctx.closePath()
}

/** Classic eighth-note silhouette */
const drawMusicNote = (ctx, x, y, scale, color, opacity = 1) => {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)
  ctx.globalAlpha = opacity
  ctx.fillStyle = color
  ctx.strokeStyle = color
  ctx.lineCap = "round"
  ctx.lineJoin = "round"

  // Note head (ellipse)
  ctx.beginPath()
  ctx.ellipse(0, 0, 7, 5.2, -0.35, 0, Math.PI * 2)
  ctx.fill()

  // Stem
  ctx.lineWidth = 2.2
  ctx.beginPath()
  ctx.moveTo(5.5, -1)
  ctx.lineTo(5.5, -28)
  ctx.stroke()

  // Flag
  ctx.beginPath()
  ctx.moveTo(5.5, -28)
  ctx.bezierCurveTo(14, -24, 18, -16, 12, -10)
  ctx.bezierCurveTo(16, -16, 12, -24, 5.5, -26)
  ctx.closePath()
  ctx.fill()

  ctx.restore()
}

/** Small beamed pair of notes */
const drawBeamedNotes = (ctx, x, y, scale, color, opacity = 1) => {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(scale, scale)
  ctx.globalAlpha = opacity
  ctx.fillStyle = color
  ctx.strokeStyle = color
  ctx.lineCap = "round"
  ctx.lineWidth = 2

  ctx.beginPath()
  ctx.ellipse(-10, 4, 5.5, 4.2, -0.35, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.ellipse(8, 0, 5.5, 4.2, -0.35, 0, Math.PI * 2)
  ctx.fill()

  ctx.beginPath()
  ctx.moveTo(-5.5, 2)
  ctx.lineTo(-5.5, -22)
  ctx.moveTo(12.5, -2)
  ctx.lineTo(12.5, -26)
  ctx.stroke()

  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.moveTo(-5.5, -22)
  ctx.lineTo(12.5, -26)
  ctx.stroke()
  ctx.lineWidth = 2.2
  ctx.beginPath()
  ctx.moveTo(-5.5, -17)
  ctx.lineTo(12.5, -21)
  ctx.stroke()

  ctx.restore()
}

/**
 * Classy acoustic guitar — body right, neck leading top-right when rotated mildly.
 * Drawn facing +X (headstock to the right).
 */
const drawGuitar = (ctx, x, y, angle, pulse, opacity, scale = 1) => {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(angle)
  ctx.scale(scale * (1 + pulse * 0.03), scale * (1 - pulse * 0.015))
  ctx.globalAlpha = opacity
  ctx.lineCap = "round"
  ctx.lineJoin = "round"

  // Soft stage glow
  const glow = ctx.createRadialGradient(0, 0, 8, 0, 0, 70)
  glow.addColorStop(0, "rgba(245, 193, 90, 0.4)")
  glow.addColorStop(0.4, "rgba(99, 145, 186, 0.18)")
  glow.addColorStop(1, "rgba(99, 145, 186, 0)")
  ctx.fillStyle = glow
  ctx.beginPath()
  ctx.arc(0, 0, 70, 0, Math.PI * 2)
  ctx.fill()

  // Body (figure-8 acoustic silhouette)
  const bodyGrad = ctx.createLinearGradient(-36, -20, 20, 28)
  bodyGrad.addColorStop(0, "#C47A2C")
  bodyGrad.addColorStop(0.45, "#E8A838")
  bodyGrad.addColorStop(0.75, "#F5C15A")
  bodyGrad.addColorStop(1, "#FFE9B0")
  ctx.fillStyle = bodyGrad
  ctx.beginPath()
  ctx.moveTo(-8, -4)
  ctx.bezierCurveTo(-28, -32, -52, -18, -48, 4)
  ctx.bezierCurveTo(-52, 26, -30, 36, -10, 18)
  ctx.bezierCurveTo(-2, 28, 14, 22, 16, 4)
  ctx.bezierCurveTo(18, -14, 4, -22, -8, -4)
  ctx.closePath()
  ctx.fill()
  ctx.strokeStyle = "rgba(120, 60, 20, 0.35)"
  ctx.lineWidth = 1.4
  ctx.stroke()

  // Sound hole
  ctx.beginPath()
  ctx.arc(-14, 2, 9, 0, Math.PI * 2)
  ctx.fillStyle = "#2b2b2f"
  ctx.fill()
  ctx.beginPath()
  ctx.arc(-14, 2, 11.5, 0, Math.PI * 2)
  ctx.strokeStyle = "rgba(255, 233, 176, 0.55)"
  ctx.lineWidth = 1.5
  ctx.stroke()

  // Bridge
  ctx.fillStyle = "#5C3A1E"
  ctx.beginPath()
  ctx.moveTo(-4, 10)
  ctx.lineTo(10, 10)
  ctx.quadraticCurveTo(12, 10, 12, 12)
  ctx.lineTo(12, 14)
  ctx.quadraticCurveTo(12, 14, 10, 14)
  ctx.lineTo(-4, 14)
  ctx.quadraticCurveTo(-6, 14, -6, 12)
  ctx.lineTo(-6, 12)
  ctx.quadraticCurveTo(-6, 10, -4, 10)
  ctx.closePath()
  ctx.fill()

  // Neck
  const neckGrad = ctx.createLinearGradient(16, 0, 78, 0)
  neckGrad.addColorStop(0, "#8B5A2B")
  neckGrad.addColorStop(1, "#5C3A1E")
  ctx.fillStyle = neckGrad
  ctx.beginPath()
  ctx.moveTo(14, -5)
  ctx.lineTo(76, -4)
  ctx.lineTo(76, 6)
  ctx.lineTo(14, 7)
  ctx.closePath()
  ctx.fill()

  // Frets
  ctx.strokeStyle = "rgba(255, 233, 176, 0.35)"
  ctx.lineWidth = 1
  for (let i = 0; i < 5; i += 1) {
    const fx = 24 + i * 10
    ctx.beginPath()
    ctx.moveTo(fx, -4)
    ctx.lineTo(fx, 6)
    ctx.stroke()
  }

  // Strings
  ctx.strokeStyle = "rgba(255, 255, 255, 0.55)"
  ctx.lineWidth = 0.8
  for (let i = 0; i < 6; i += 1) {
    const sy = -3 + i * 1.8
    ctx.beginPath()
    ctx.moveTo(-2, sy + 12)
    ctx.lineTo(76, sy + 0.5)
    ctx.stroke()
  }

  // Headstock
  ctx.fillStyle = "#5C3A1E"
  ctx.beginPath()
  ctx.moveTo(74, -6)
  ctx.lineTo(92, -10)
  ctx.lineTo(94, -2)
  ctx.lineTo(90, 8)
  ctx.lineTo(74, 7)
  ctx.closePath()
  ctx.fill()

  // Tuning pegs
  ctx.fillStyle = "#F5C15A"
  ;[
    [84, -8],
    [88, -6],
    [86, 4],
    [90, 6],
  ].forEach(([px, py]) => {
    ctx.beginPath()
    ctx.arc(px, py, 2.2, 0, Math.PI * 2)
    ctx.fill()
  })

  // Highlight
  ctx.strokeStyle = "rgba(255, 255, 255, 0.25)"
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(-36, -8)
  ctx.bezierCurveTo(-24, -18, -8, -14, 4, -6)
  ctx.stroke()

  ctx.restore()
}

/**
 * Run the musical celebration on a full-viewport canvas.
 * Returns a cancel function.
 */
export const runMusicalCelebration = (canvas, { onComplete } = {}) => {
  if (!canvas) return () => {}

  const ctx = canvas.getContext("2d")
  if (!ctx) return () => {}

  let raf = 0
  let cancelled = false

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const w = window.innerWidth
    const h = window.innerHeight
    canvas.width = Math.floor(w * dpr)
    canvas.height = Math.floor(h * dpr)
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  resize()
  window.addEventListener("resize", resize)

  if (prefersReducedMotion()) {
    const timer = window.setTimeout(() => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      onComplete?.()
    }, 400)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
      window.removeEventListener("resize", resize)
    }
  }

  const W = () => window.innerWidth
  const H = () => window.innerHeight

  const particles = []
  const glitter = []
  const sparks = []
  let hasBurst = false
  let start = null

  /** Guitar leans toward top-right without flipping onto its side */
  const GUITAR_ANGLE = -Math.PI / 5

  const emitTrail = (pos, angle) => {
    for (let i = 0; i < 3; i += 1) {
      const spread = (Math.random() - 0.5) * 36
      const back = 40 + Math.random() * 30
      particles.push({
        x: pos.x - Math.cos(angle) * back + spread,
        y: pos.y - Math.sin(angle) * back + spread * 0.45,
        vx:
          -Math.cos(angle) * (0.8 + Math.random() * 1.8) +
          (Math.random() - 0.5) * 1.2,
        vy:
          -Math.sin(angle) * (0.8 + Math.random() * 1.8) +
          (Math.random() - 0.5) * 1.2 -
          0.4,
        size: 0.55 + Math.random() * 0.55,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        life: 1,
        decay: 0.014 + Math.random() * 0.022,
        kind: Math.random() > 0.55 ? "note" : Math.random() > 0.5 ? "beam" : "dot",
        spin: (Math.random() - 0.5) * 0.08,
        rot: Math.random() * Math.PI,
      })
    }
  }

  const createBurst = (bx, by) => {
    for (let i = 0; i < 56; i += 1) {
      const a = Math.random() * Math.PI * 2
      const speed = 2 + Math.random() * 9
      glitter.push({
        x: bx,
        y: by,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed - 1.6,
        size: 0.45 + Math.random() * 0.7,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rot: Math.random() * Math.PI,
        vRot: (Math.random() - 0.5) * 0.2,
        opacity: 1,
        gravity: 0.14,
        drag: 0.97,
        kind: Math.random() > 0.4 ? "note" : Math.random() > 0.5 ? "beam" : "star",
      })
    }
    for (let i = 0; i < 32; i += 1) {
      const a = Math.random() * Math.PI * 2
      const speed = 1.2 + Math.random() * 7
      sparks.push({
        x: bx,
        y: by,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed,
        size: 4 + Math.random() * 12,
        color: COLORS[Math.floor(Math.random() * 6)],
        opacity: 1,
        decay: 0.012 + Math.random() * 0.018,
      })
    }
  }

  const frame = now => {
    if (cancelled) return
    if (start == null) start = now

    const w = W()
    const h = H()
    const elapsed = now - start
    const flightT = Math.min(elapsed / FLIGHT_MS, 1)
    const progress = easeInOut(flightT)

    ctx.clearRect(0, 0, w, h)

    const p0 = { x: -120, y: h * 0.9 }
    const p1 = { x: w * 0.2, y: h * 0.95 }
    const p2 = { x: w * 0.52, y: h * 0.1 }
    const p3 = { x: w * 0.94, y: h * 0.12 }

    if (flightT < 1) {
      const pos = cubicBezier(progress, p0, p1, p2, p3)
      const { dx, dy } = cubicBezierDerivative(progress, p0, p1, p2, p3)
      const trailAngle = Math.atan2(dy, dx)
      const pulse = Math.sin(elapsed * 0.012)

      emitTrail(pos, trailAngle)

      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i]
        p.x += p.vx
        p.y += p.vy
        p.rot += p.spin
        p.life -= p.decay
        if (p.life <= 0) {
          particles.splice(i, 1)
          continue
        }
        if (p.kind === "note") {
          ctx.save()
          ctx.translate(p.x, p.y)
          ctx.rotate(p.rot)
          drawMusicNote(ctx, 0, 0, p.size, p.color, p.life * 0.95)
          ctx.restore()
        } else if (p.kind === "beam") {
          ctx.save()
          ctx.translate(p.x, p.y)
          ctx.rotate(p.rot * 0.5)
          drawBeamedNotes(ctx, 0, 0, p.size * 0.85, p.color, p.life * 0.9)
          ctx.restore()
        } else {
          ctx.save()
          ctx.globalAlpha = p.life * 0.9
          ctx.fillStyle = p.color
          ctx.beginPath()
          ctx.arc(p.x, p.y, 2.5 * p.size, 0, Math.PI * 2)
          ctx.fill()
          ctx.restore()
        }
      }

      let opacity = 1
      if (progress < 0.07) opacity = progress / 0.07
      if (progress > 0.84) opacity = (1 - progress) / 0.16

      const guitarScale = Math.min(1.15, Math.max(0.78, w / 1400))
      drawGuitar(ctx, pos.x, pos.y, GUITAR_ANGLE, pulse, opacity, guitarScale)

      // Companion notes orbiting the guitar
      for (let i = 0; i < 3; i += 1) {
        const a = elapsed * 0.004 + i * ((Math.PI * 2) / 3)
        const ox = Math.cos(a) * (38 + i * 6)
        const oy = Math.sin(a) * (22 + i * 4) - 8
        drawMusicNote(
          ctx,
          pos.x + ox,
          pos.y + oy,
          0.7 + i * 0.08,
          COLORS[i % COLORS.length],
          opacity * 0.85,
        )
      }

      if (progress >= 0.82 && !hasBurst) {
        hasBurst = true
        createBurst(pos.x, pos.y)
      }
    } else if (!hasBurst) {
      hasBurst = true
      createBurst(w * 0.92, h * 0.14)
    }

    for (let i = glitter.length - 1; i >= 0; i -= 1) {
      const c = glitter[i]
      c.vx *= c.drag
      c.vy *= c.drag
      c.vy += c.gravity
      c.x += c.vx
      c.y += c.vy
      c.rot += c.vRot
      c.opacity -= 0.006
      if (c.opacity <= 0 || c.y > h + 40) {
        glitter.splice(i, 1)
        continue
      }
      if (c.kind === "note") {
        ctx.save()
        ctx.translate(c.x, c.y)
        ctx.rotate(c.rot)
        drawMusicNote(ctx, 0, 0, c.size, c.color, Math.max(0, c.opacity))
        ctx.restore()
      } else if (c.kind === "beam") {
        ctx.save()
        ctx.translate(c.x, c.y)
        ctx.rotate(c.rot)
        drawBeamedNotes(ctx, 0, 0, c.size * 0.8, c.color, Math.max(0, c.opacity))
        ctx.restore()
      } else {
        ctx.save()
        ctx.globalAlpha = Math.max(0, c.opacity)
        ctx.fillStyle = c.color
        ctx.shadowColor = c.color
        ctx.shadowBlur = 8
        drawStar(ctx, c.x, c.y, 6 * c.size, 2.5 * c.size)
        ctx.fill()
        ctx.restore()
      }
    }

    for (let i = sparks.length - 1; i >= 0; i -= 1) {
      const s = sparks[i]
      s.x += s.vx
      s.y += s.vy
      s.vx *= 0.98
      s.vy *= 0.98
      s.opacity -= s.decay
      if (s.opacity <= 0) {
        sparks.splice(i, 1)
        continue
      }
      ctx.save()
      ctx.globalAlpha = Math.max(0, s.opacity)
      ctx.fillStyle = s.color
      ctx.shadowColor = s.color
      ctx.shadowBlur = 10
      drawStar(ctx, s.x, s.y, s.size, s.size * 0.35)
      ctx.fill()
      ctx.restore()
    }

    const stillAlive =
      flightT < 1 ||
      glitter.length > 0 ||
      sparks.length > 0 ||
      particles.length > 0 ||
      elapsed < FLIGHT_MS + SETTLE_MS

    if (stillAlive) {
      raf = requestAnimationFrame(frame)
    } else {
      ctx.clearRect(0, 0, w, h)
      window.removeEventListener("resize", resize)
      onComplete?.()
    }
  }

  raf = requestAnimationFrame(frame)

  return () => {
    cancelled = true
    cancelAnimationFrame(raf)
    window.removeEventListener("resize", resize)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }
}
